import React, { useState } from 'react';

const AdvertForm = ({ fetchPosts, posts, showPosts, setShowPosts, onLogout, onReply }) => {
  const [formData, setFormData] = useState({
    packageType: '',
    collectionPoint: '',
    dropOffPoint: '',
    collectionDate: '',
    deliveryDate: '',
    parcelWeight: '',
    palletWeight: '',
    volumesInTubes: '',
    numberOfPallets: '',
    numberOfParcels: '',
    totalWeight: '',
    dateQuoteBy: '',
    closingDate: '',
  });
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [appliedCompanies, setAppliedCompanies] = useState([]);
  const [showAppliedCompanies, setShowAppliedCompanies] = useState(false);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [responseStatus, setResponseStatus] = useState(null); // 'accepted' or 'rejected'
  const [responseDetails, setResponseDetails] = useState({
    reason: '',
    contact_name: '',
    contact_phone: '',
    collection_instructions: '',
  });

  const calculateTotalWeight = (data) => {
    const parcelTotal = parseFloat(data.parcelWeight || 0) * parseFloat(data.numberOfParcels || 0);
    const palletTotal = parseFloat(data.palletWeight || 0) * parseFloat(data.numberOfPallets || 0);
    return (parcelTotal + palletTotal).toFixed(1);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedFormData = { ...formData, [name]: value };
    if (['parcelWeight', 'numberOfParcels', 'palletWeight', 'numberOfPallets'].includes(name)) {
      updatedFormData.totalWeight = calculateTotalWeight(updatedFormData);
    }
    setFormData(updatedFormData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const quoteDate = new Date(formData.dateQuoteBy);
    const closeDate = new Date(formData.closingDate);
    const collectionDate = formData.collectionDate ? new Date(formData.collectionDate) : null;
    const deliveryDate = formData.deliveryDate ? new Date(formData.deliveryDate) : null;
    const today = new Date();

    if (isNaN(quoteDate.getTime())) {
      alert('Please select a valid quote date and time');
      setIsSubmitting(false);
      return;
    }
    if (isNaN(closeDate.getTime())) {
      alert('Please select a valid closing date and time');
      setIsSubmitting(false);
      return;
    }
    if (quoteDate < today) {
      alert('Quote date and time must be in the future');
      setIsSubmitting(false);
      return;
    }
    if (closeDate < quoteDate) {
      alert('Closing date and time must be after quote date and time');
      setIsSubmitting(false);
      return;
    }
    if (collectionDate && deliveryDate && deliveryDate < collectionDate) {
      alert('Delivery date and time must be after collection date and time');
      setIsSubmitting(false);
      return;
    }

    const calculatedTotal = calculateTotalWeight(formData);
    if (Math.abs(calculatedTotal - parseFloat(formData.totalWeight || 0)) > 0.1) {
      alert('Total weight does not match calculated weight. It has been updated automatically.');
      setFormData({ ...formData, totalWeight: calculatedTotal });
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch('https://dropquote.co.za/backend/save_advert.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          collectionDate: formData.collectionDate ? formData.collectionDate.split('T')[0] : '',
          deliveryDate: formData.deliveryDate ? formData.deliveryDate.split('T')[0] : '',
          dateQuoteBy: formData.dateQuoteBy.split('T')[0],
          closingDate: formData.closingDate.split('T')[0],
        }),
      });

      const rawText = await response.text();
      console.log('Raw server response:', rawText);

      let result;
      try {
        result = JSON.parse(rawText);
      } catch (jsonError) {
        throw new Error('Invalid JSON response from server: ' + rawText);
      }

      if (result.success) {
        alert(result.message);
        setFormData({
          packageType: '',
          collectionPoint: '',
          dropOffPoint: '',
          collectionDate: '',
          deliveryDate: '',
          parcelWeight: '',
          palletWeight: '',
          volumesInTubes: '',
          numberOfPallets: '',
          numberOfParcels: '',
          totalWeight: '',
          dateQuoteBy: '',
          closingDate: '',
        });
        setIsFormVisible(false);
        fetchPosts();
      } else {
        alert(result.message || 'Failed to post advertisement');
      }
    } catch (error) {
      console.error('Submission error:', error);
      alert(`Error submitting form: ${error.message.includes('Failed to fetch') ? 'Failed to connect to server. Check CORS or server status.' : error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const fetchAppliedCompanies = async () => {
    try {
      setIsSubmitting(true);
      const response = await fetch('https://dropquote.co.za/backend/fetch_applied_companies.php', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const rawText = await response.text();
      console.log('Raw server response:', rawText);

      let result;
      try {
        result = JSON.parse(rawText);
      } catch (jsonError) {
        throw new Error('Invalid JSON response from server: ' + rawText);
      }

      if (result.success) {
        console.log('Applied companies:', result.data);
        setAppliedCompanies(result.data);
        setShowAppliedCompanies(true);
        setShowPosts(false);
      } else {
        alert(result.message || 'Failed to fetch applied companies');
      }
    } catch (error) {
      console.error('Fetch applied companies error:', error);
      alert(`Error fetching applied companies: ${error.message.includes('Failed to fetch') ? 'Failed to connect to server. Check CORS or server status.' : error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenReplyModal = (application) => {
    console.log('Opening modal for application:', application);
    if (!application.id || !application.company_id) {
      alert('Error: Application ID or Company ID is missing');
      return;
    }
    setSelectedApplication(application);
    setShowReplyModal(true);
    setResponseStatus(null);
    setResponseDetails({
      reason: '',
      contact_name: '',
      contact_phone: '',
      collection_instructions: '',
    });
  };

  const handleCloseReplyModal = () => {
    setShowReplyModal(false);
    setSelectedApplication(null);
    setResponseStatus(null);
    setResponseDetails({
      reason: '',
      contact_name: '',
      contact_phone: '',
      collection_instructions: '',
    });
  };

  const handleResponseChange = (e) => {
    const { name, value } = e.target;
    setResponseDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmitResponse = async () => {
    if (!responseStatus) {
      alert('Please select Accept or Reject');
      return;
    }
    if (responseStatus === 'rejected' && !responseDetails.reason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }
    if (responseStatus === 'accepted' && (!responseDetails.contact_name.trim() || !responseDetails.contact_phone.trim())) {
      alert('Please provide contact name and phone for acceptance');
      return;
    }

    setIsSubmitting(true);
    try {
      await onReply(selectedApplication.id, responseStatus, {
        ...responseDetails,
        company_id: selectedApplication.company_id,
        advert_id: selectedApplication.advert_id,
        collection_point: selectedApplication.collection_point,
        drop_off_point: selectedApplication.drop_off_point,
        company_name: selectedApplication.company_name,
        quote_price: selectedApplication.quote_price,
        estimated_delivery_days: selectedApplication.estimated_delivery_days,
        notes: selectedApplication.notes,
        invoice_path: selectedApplication.invoice_path,
        applied_at: selectedApplication.applied_at,
      });
      alert('Response submitted successfully');
      handleCloseReplyModal();
      fetchAppliedCompanies();
    } catch (error) {
      console.error('Response submission error:', error);
      alert(`Error submitting response: ${error.message.includes('Failed to fetch') ? 'Failed to connect to server. Check CORS or server status.' : error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container-fluid">
      <style>
        {`
          .bg-black { background-color: #000000; color: #ffffff; }
          .btn-red { background-color: #ff0000; color: #ffffff; border: none; }
          .btn-red:hover { background-color: #cc0000; color: #ffffff; }
          .btn-white { background-color: #ffffff; color: #000000; border: 1px solid #000000; }
          .btn-white:hover { background-color: #f0f0f0; color: #000000; }
          .btn-red-outline { background-color: transparent; color: #ff0000; border: 1px solid #ff0000; }
          .btn-red-outline:hover { background-color: #ff0000; color: #ffffff; }
          .text-black { color: #000000; }
          .text-red { color: #ff0000; }
        `}
      </style>
      <div className="row">
        <div className="col-md-3 col-lg-2 bg-black p-3 min-vh-100">
          <h3 className="mb-4 text-white">
            <i className="bi bi-gear me-2"></i>Admin Menu
          </h3>
          <button
            className={`btn ${isFormVisible ? 'btn-red' : 'btn-red'} w-100 mb-3`}
            onClick={() => setIsFormVisible(!isFormVisible)}
            disabled={isSubmitting}
          >
            <i className={`bi ${isFormVisible ? 'bi-x' : 'bi-plus'} me-2`}></i>
            {isFormVisible ? 'Close Form' : 'Post New Advert'}
          </button>
          <button
            className="btn btn-white w-100 mb-3"
            onClick={fetchPosts}
            disabled={isSubmitting}
          >
            <i className="bi bi-eye me-2"></i>View Posted Ads
          </button>
          <button
            className="btn btn-white w-100 mb-3"
            onClick={fetchAppliedCompanies}
            disabled={isSubmitting}
          >
            <i className="bi bi-building me-2"></i>View Applied Companies
          </button>
          <button
            className="btn btn-white w-100"
            onClick={onLogout}
            disabled={isSubmitting}
          >
            <i className="bi bi-box-arrow-right me-2"></i>Logout
          </button>
        </div>

        <div className="col-md-9 col-lg-10 p-4">
          {isFormVisible && (
            <div className="card shadow">
              <div className="card-header bg-black text-white">
                <h2 className="mb-0">
                  <i className="bi bi-truck me-2"></i>Create Logistics Collection Advertisement
                </h2>
              </div>
              <div className="card-body bg-white">
                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label htmlFor="packageType" className="form-label text-black">
                      <i className="bi bi-box-seam me-2"></i>Package Type:
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="packageType"
                      name="packageType"
                      value={formData.packageType}
                      onChange={handleChange}
                      placeholder="e.g., Fragile, Perishable, Hazardous"
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="collectionPoint" className="form-label text-black">
                      <i className="bi bi-geo-alt me-2"></i>Collection Point:
                    </label>
                    <textarea
                      className="form-control"
                      id="collectionPoint"
                      name="collectionPoint"
                      value={formData.collectionPoint}
                      onChange={handleChange}
                      placeholder="e.g., Warehouse A, 123 Industrial Road, Pinetown, SA\nContact: John, +27 123 456 7890"
                      rows="4"
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="dropOffPoint" className="form-label text-black">
                      <i className="bi bi-geo me-2"></i>Drop-off Point:
                    </label>
                    <textarea
                      className="form-control"
                      id="dropOffPoint"
                      name="dropOffPoint"
                      value={formData.dropOffPoint}
                      onChange={handleChange}
                      placeholder="e.g., Depot B, 456 Logistics Lane, Umngeni, SA\nContact: Jane, +27 123 456 7890"
                      rows="4"
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="collectionDate" className="form-label text-black">
                      <i className="bi bi-calendar-event me-2"></i>Collection Date & Time:
                    </label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      id="collectionDate"
                      name="collectionDate"
                      value={formData.collectionDate}
                      onChange={handleChange}
                      min={new Date().toISOString().slice(0, 16)}
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="deliveryDate" className="form-label text-black">
                      <i className="bi bi-calendar-check me-2"></i>Delivery Date & Time:
                    </label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      id="deliveryDate"
                      name="deliveryDate"
                      value={formData.deliveryDate}
                      onChange={handleChange}
                      min={formData.collectionDate || new Date().toISOString().slice(0, 16)}
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="parcelWeight" className="form-label text-black">
                      <i className="bi bi-box me-2"></i>Average Parcel Weight (kg):
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      id="parcelWeight"
                      name="parcelWeight"
                      value={formData.parcelWeight}
                      onChange={handleChange}
                      min="0"
                      step="0.1"
                      placeholder="e.g., 2.5"
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="palletWeight" className="form-label text-black">
                      <i className="bi bi-stack me-2"></i>Average Pallet Weight (kg):
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      id="palletWeight"
                      name="palletWeight"
                      value={formData.palletWeight}
                      onChange={handleChange}
                      min="0"
                      step="0.1"
                      placeholder="e.g., 250.0"
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="volumesInTubes" className="form-label text-black">
                      <i className="bi bi-circle me-2"></i>Volumes in Tubes (m³):
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      id="volumesInTubes"
                      name="volumesInTubes"
                      value={formData.volumesInTubes}
                      onChange={handleChange}
                      min="0"
                      step="0.01"
                      placeholder="e.g., 50.25"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="numberOfPallets" className="form-label text-black">
                      <i className="bi bi-grid me-2"></i>Number of Pallets:
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      id="numberOfPallets"
                      name="numberOfPallets"
                      value={formData.numberOfPallets}
                      onChange={handleChange}
                      min="0"
                      placeholder="e.g., 10"
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="numberOfParcels" className="form-label text-black">
                      <i className="bi bi-boxes me-2"></i>Number of Parcels:
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      id="numberOfParcels"
                      name="numberOfParcels"
                      value={formData.numberOfParcels}
                      onChange={handleChange}
                      min="0"
                      placeholder="e.g., 25"
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="totalWeight" className="form-label text-black">
                      <i className="bi bi-scales me-2"></i>Total Weight (kg):
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      id="totalWeight"
                      name="totalWeight"
                      value={formData.totalWeight}
                      onChange={handleChange}
                      min="0"
                      step="0.1"
                      placeholder="Automatically calculated"
                      readOnly
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="dateQuoteBy" className="form-label text-black">
                      <i className="bi bi-calendar-check me-2"></i>Quote By Date & Time:
                    </label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      id="dateQuoteBy"
                      name="dateQuoteBy"
                      value={formData.dateQuoteBy}
                      onChange={handleChange}
                      min={new Date().toISOString().slice(0, 16)}
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="closingDate" className="form-label text-black">
                      <i className="bi bi-calendar-x me-2"></i>Advertisement Closing Date & Time:
                    </label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      id="closingDate"
                      name="closingDate"
                      value={formData.closingDate}
                      onChange={handleChange}
                      min={formData.dateQuoteBy || new Date().toISOString().slice(0, 16)}
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                  <button
                    type="submit"
                    className="btn btn-red w-100"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-send me-2"></i>Post Logistics Advert
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          )}
          {showPosts && (
            <div className="mt-4">
              <h2 className="text-center text-black">Advertised Posts</h2>
              <button
                className="btn btn-red mb-3"
                onClick={() => setShowPosts(false)}
                disabled={isSubmitting}
              >
                <i className="bi bi-x me-2"></i>Close
              </button>
              {posts.length > 0 ? (
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Collection Point</th>
                      <th>Drop-off Point</th>
                      <th>Collection Date</th>
                      <th>Delivery Date</th>
                      <th>Parcel Weight (kg)</th>
                      <th>Pallet Weight (kg)</th>
                      <th>Volumes (m³)</th>
                      <th>Pallets</th>
                      <th>Parcels</th>
                      <th>Total Weight (kg)</th>
                      <th>Number of Responses</th>
                      <th>Closing Date</th>
                      <th>Created At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {posts.map((post) => (
                      <tr key={post.id}>
                        <td>{post.id}</td>
                        <td>{post.collection_point}</td>
                        <td>{post.drop_off_point}</td>
                        <td>{post.collection_date || 'N/A'}</td>
                        <td>{post.delivery_date || 'N/A'}</td>
                        <td>{post.parcel_weight}</td>
                        <td>{post.pallet_weight}</td>
                        <td>{post.volumes_in_tubes || 'N/A'}</td>
                        <td>{post.number_of_pallets}</td>
                        <td>{post.number_of_parcels}</td>
                        <td>{post.total_weight}</td>
                        <td>{post.number_of_responses}</td>
                        <td>{post.closing_date}</td>
                        <td>{post.created_date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-black">No advertisements found.</p>
              )}
            </div>
          )}
          {showAppliedCompanies && (
            <div className="mt-4">
              <h2 className="text-center text-black">Applied Companies</h2>
              <button
                className="btn btn-red mb-3"
                onClick={() => setShowAppliedCompanies(false)}
                disabled={isSubmitting}
              >
                <i className="bi bi-x me-2"></i>Close
              </button>
              {appliedCompanies.length > 0 ? (
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Advert Number</th>
                      <th>Collection Point</th>
                      <th>Drop-off Point</th>
                      <th>Company Name</th>
                      <th>Quote Price</th>
                      <th>Estimated Delivery Days</th>
                      <th>Notes</th>
                      <th>Invoice</th>
                      <th>Applied At</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {appliedCompanies.map((app) => (
                      <tr key={app.id}>
                        <td>{app.advert_id}</td>
                        <td>{app.collection_point}</td>
                        <td>{app.drop_off_point}</td>
                        <td>{app.company_name}</td>
                        <td>{app.quote_price}</td>
                        <td>{app.estimated_delivery_days}</td>
                        <td>{app.notes || 'N/A'}</td>
                        <td>
                          {app.invoice_path ? (
                            <a
                              href={'https://dropquote.co.za/backend/' + app.invoice_path}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-red"
                            >
                              View PDF
                            </a>
                          ) : (
                            'N/A'
                          )}
                        </td>
                        <td>{app.applied_at}</td>
                        <td>{app.status || 'Pending'}</td>
                        <td>
                          <button
                            onClick={() => handleOpenReplyModal(app)}
                            className={`btn btn-sm ${app.status ? 'btn-secondary' : 'btn-red'}`}
                            disabled={app.status || isSubmitting}
                          >
                            {app.status ? (
                              <>
                                <i className="bi bi-check-circle me-2"></i>Already Replied
                              </>
                            ) : (
                              <>
                                <i className="bi bi-reply me-2"></i>Reply
                              </>
                            )}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-black">No companies have applied.</p>
              )}
            </div>
          )}

          {/* Reply Modal */}
          {showReplyModal && selectedApplication && (
            <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
              <div className="modal-dialog modal-lg">
                <div className="modal-content">
                  <div className="modal-header bg-black text-white">
                    <h5 className="modal-title">Reply to Application</h5>
                    <button
                      type="button"
                      className="btn-close btn-close-white"
                      onClick={handleCloseReplyModal}
                      disabled={isSubmitting}
                    ></button>
                  </div>
                  <div className="modal-body bg-white">
                    <h6 className="text-black">Application Details</h6>
                    <table className="table table-bordered">
                      <tbody>
                        <tr>
                          <th>Advert Number</th>
                          <td>{selectedApplication.advert_id || 'N/A'}</td>
                        </tr>
                        <tr>
                          <th>Collection Point</th>
                          <td>{selectedApplication.collection_point || 'N/A'}</td>
                        </tr>
                        <tr>
                          <th>Drop-off Point</th>
                          <td>{selectedApplication.drop_off_point || 'N/A'}</td>
                        </tr>
                        <tr>
                          <th>Company Name</th>
                          <td>{selectedApplication.company_name || 'N/A'}</td>
                        </tr>
                        <tr>
                          <th>Company ID</th>
                          <td>{selectedApplication.company_id || 'N/A'}</td>
                        </tr>
                        <tr>
                          <th>Quote Price</th>
                          <td>{selectedApplication.quote_price || 'N/A'}</td>
                        </tr>
                        <tr>
                          <th>Estimated Delivery Days</th>
                          <td>{selectedApplication.estimated_delivery_days || 'N/A'}</td>
                        </tr>
                        <tr>
                          <th>Notes</th>
                          <td>{selectedApplication.notes || 'N/A'}</td>
                        </tr>
                        <tr>
                          <th>Invoice</th>
                          <td>
                            {selectedApplication.invoice_path ? (
                              <a
                                href={'https://dropquote.co.za/backend/' + selectedApplication.invoice_path}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-red"
                              >
                                View PDF
                              </a>
                            ) : (
                              'N/A'
                            )}
                          </td>
                        </tr>
                        <tr>
                          <th>Applied At</th>
                          <td>{selectedApplication.applied_at || 'N/A'}</td>
                        </tr>
                      </tbody>
                    </table>

                    <div className="mt-3">
                      <h6 className="text-black">Response</h6>
                      {/* Hidden input for company_id */}
                      <input
                        type="hidden"
                        id="company_id"
                        name="company_id"
                        value={selectedApplication.company_id || ''}
                      />
                      <div className="mb-3">
                        <button
                          className={`btn ${responseStatus === 'accepted' ? 'btn-red' : 'btn-red-outline'} me-2`}
                          onClick={() => setResponseStatus('accepted')}
                          disabled={isSubmitting}
                        >
                          Accept
                        </button>
                        <button
                          className={`btn ${responseStatus === 'rejected' ? 'btn-red' : 'btn-red-outline'}`}
                          onClick={() => setResponseStatus('rejected')}
                          disabled={isSubmitting}
                        >
                          Reject
                        </button>
                      </div>

                      {responseStatus === 'rejected' && (
                        <div className="mb-3">
                          <label htmlFor="reason" className="form-label text-black">Reason for Rejection</label>
                          <textarea
                            className="form-control"
                            id="reason"
                            name="reason"
                            value={responseDetails.reason}
                            onChange={handleResponseChange}
                            rows="4"
                            placeholder="Please specify the reason for rejecting this application"
                            disabled={isSubmitting}
                            required
                          />
                        </div>
                      )}

                      {responseStatus === 'accepted' && (
                        <div>
                          <div className="mb-3">
                            <label htmlFor="contact_name" className="form-label text-black">Contact Name</label>
                            <input
                              type="text"
                              className="form-control"
                              id="contact_name"
                              name="contact_name"
                              value={responseDetails.contact_name}
                              onChange={handleResponseChange}
                              placeholder="e.g., John Doe"
                              disabled={isSubmitting}
                              required
                            />
                          </div>
                          <div className="mb-3">
                            <label htmlFor="contact_phone" className="form-label text-black">Contact Phone</label>
                            <input
                              type="tel"
                              className="form-control"
                              id="contact_phone"
                              name="contact_phone"
                              value={responseDetails.contact_phone}
                              onChange={handleResponseChange}
                              placeholder="e.g., +27 123 456 7890"
                              disabled={isSubmitting}
                              required
                            />
                          </div>
                          <div className="mb-3">
                            <label htmlFor="collection_instructions" className="form-label text-black">Collection Instructions</label>
                            <textarea
                              className="form-control"
                              id="collection_instructions"
                              name="collection_instructions"
                              value={responseDetails.collection_instructions}
                              onChange={handleResponseChange}
                              rows="4"
                              placeholder="e.g., Contact warehouse manager upon arrival"
                              disabled={isSubmitting}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="modal-footer bg-white">
                    <button
                      type="button"
                      className="btn btn-white"
                      onClick={handleCloseReplyModal}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-red"
                      onClick={handleSubmitResponse}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Submitting...
                        </>
                      ) : (
                        'Submit Response'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdvertForm;